import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, User, Clock, AlertCircle } from 'lucide-react'

interface UserDetails {
  id: string
  email: string | null
  full_name: string | null
  status: string
  created_at: string
  action: string
}

type ApprovalState = 'loading' | 'ready' | 'confirming' | 'success' | 'error' | 'already_processed'

const API_BASE = '/api'

export function AdminApprovalPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const userId = searchParams.get('user_id')
  const token = searchParams.get('token')
  const action = searchParams.get('action') // 'approve' or 'reject'

  const [state, setState] = useState<ApprovalState>('loading')
  const [user, setUser] = useState<UserDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resultMessage, setResultMessage] = useState<string | null>(null)

  // Fetch user details on mount
  useEffect(() => {
    if (!userId || !token || !action) {
      setError('Missing required parameters')
      setState('error')
      return
    }

    async function fetchUser() {
      try {
        const response = await fetch(
          `${API_BASE}/admin/user/${userId}?token=${token}&action=${action}`
        )

        if (!response.ok) {
          if (response.status === 403) {
            setError('Invalid or expired approval link')
          } else if (response.status === 404) {
            setError('User not found')
          } else {
            setError('Failed to load user details')
          }
          setState('error')
          return
        }

        const data = await response.json()
        setUser(data)

        // Check if already processed
        if (data.status !== 'pending') {
          setState('already_processed')
        } else {
          setState('ready')
        }
      } catch (err) {
        setError('Network error. Please try again.')
        setState('error')
      }
    }

    fetchUser()
  }, [userId, token, action])

  const handleConfirm = async () => {
    if (!userId || !token || !action) return

    setState('confirming')

    try {
      const endpoint = action === 'approve' ? 'approve' : 'reject'
      const response = await fetch(
        `${API_BASE}/admin/${endpoint}/${userId}?token=${token}`,
        { method: 'POST' }
      )

      if (!response.ok) {
        const data = await response.json()
        setError(data.detail || 'Failed to process request')
        setState('error')
        return
      }

      const data = await response.json()
      setResultMessage(data.message)
      setState('success')
    } catch (err) {
      setError('Network error. Please try again.')
      setState('error')
    }
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Loading user details...
          </p>
        </div>
      </div>
    )
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-light tracking-tight text-foreground mb-4">
            Error
          </h1>
          <p className="text-muted-foreground font-light mb-8">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-md border border-border hover:bg-muted text-sm font-light transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  // Already processed state
  if (state === 'already_processed' && user) {
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-6">
            <User className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-light tracking-tight text-foreground mb-4">
            Already Processed
          </h1>
          <p className="text-muted-foreground font-light mb-2">
            This user has already been{' '}
            <span className={user.status === 'approved' ? 'text-green-600' : 'text-red-600'}>
              {user.status}
            </span>.
          </p>
          <p className="text-sm text-muted-foreground font-light mb-8">
            {user.email}
          </p>
        </div>
      </div>
    )
  }

  // Success state
  if (state === 'success') {
    const isApprove = action === 'approve'
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
            isApprove ? 'bg-green-500/10' : 'bg-red-500/10'
          }`}>
            {isApprove ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <XCircle className="w-8 h-8 text-red-500" />
            )}
          </div>
          <h1 className="text-2xl font-light tracking-tight text-foreground mb-4">
            {isApprove ? 'User Approved' : 'User Rejected'}
          </h1>
          <p className="text-muted-foreground font-light mb-8">
            {resultMessage}
          </p>
        </div>
      </div>
    )
  }

  // Confirming state (processing)
  if (state === 'confirming') {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Processing...
          </p>
        </div>
      </div>
    )
  }

  // Ready state - show confirmation
  const isApprove = action === 'approve'
  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
            isApprove ? 'bg-green-500/10' : 'bg-red-500/10'
          }`}>
            {isApprove ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <XCircle className="w-8 h-8 text-red-500" />
            )}
          </div>
          <h1 className="text-2xl font-light tracking-tight text-foreground mb-2">
            {isApprove ? 'Approve User' : 'Reject User'}
          </h1>
          <p className="text-muted-foreground font-light">
            {isApprove
              ? 'Grant this user access to the application?'
              : 'Deny this user access to the application?'}
          </p>
        </div>

        {/* User details card */}
        {user && (
          <div className="mb-8 p-6 rounded-lg border border-border bg-card">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground">
                    {user.full_name || 'No name provided'}
                  </p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>
                  Signed up {new Date(user.created_at).toLocaleDateString()} at{' '}
                  {new Date(user.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={handleConfirm}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-white transition-colors ${
              isApprove
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isApprove ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Yes, Approve User</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Yes, Reject User</span>
              </>
            )}
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-3 rounded-md border border-border hover:bg-muted text-sm font-light transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
